import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ConfirmDialogIntent = 'danger' | 'warning' | 'primary' | 'neutral';
export type ConfirmDialogIcon = 'trash' | 'logout' | 'warning' | 'info';

export interface ConfirmDialogOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  intent?: ConfirmDialogIntent;
  icon?: ConfirmDialogIcon;
}

export interface ConfirmDialogState extends ConfirmDialogOptions {
  open: boolean;
}

@Injectable({ providedIn: 'root' })
export class ConfirmDialogService {
  private readonly stateSubject = new BehaviorSubject<ConfirmDialogState>({
    open: false,
    title: '',
    message: '',
    confirmText: 'Confirmar',
    cancelText: 'Cancelar',
    intent: 'danger',
    icon: 'warning',
  });

  private pendingResolve: ((value: boolean) => void) | null = null;

  getState(): Observable<ConfirmDialogState> {
    return this.stateSubject.asObservable();
  }

  confirm(options: ConfirmDialogOptions): Promise<boolean> {
    if (this.pendingResolve) {
      this.pendingResolve(false);
      this.pendingResolve = null;
    }

    const state: ConfirmDialogState = {
      open: true,
      title: options.title,
      message: options.message,
      confirmText: options.confirmText || 'Confirmar',
      cancelText: options.cancelText || 'Cancelar',
      intent: options.intent || 'danger',
      icon: options.icon || 'warning',
    };

    this.stateSubject.next(state);

    return new Promise((resolve) => {
      this.pendingResolve = resolve;
    });
  }

  accept(): void {
    if (this.pendingResolve) {
      this.pendingResolve(true);
      this.pendingResolve = null;
    }
    this.close();
  }

  cancel(): void {
    if (this.pendingResolve) {
      this.pendingResolve(false);
      this.pendingResolve = null;
    }
    this.close();
  }

  close(): void {
    this.stateSubject.next({
      ...this.stateSubject.getValue(),
      open: false,
    });
  }
}
